// Chat do Assistente IA (BETA, 2026-10-20) — usado na tela do app do
// cliente (mobile-ai-assistant.tsx) e no "Testar conversa" da
// Administração. Texto e áudio gravado na hora (anexo/câmera saíram em
// 2026-10-21 a pedido do usuário).
// A resposta chega em segundo plano: enquanto houver mensagem "pending" a
// conversa é recarregada a cada 1,5 s.
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useAuth } from "@/contexts/auth-context";
import { aiPalette } from "./ai-palette";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, FileText, Loader2, Mic, Send, Square, Trash2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { GpsDriveApiError, resolveGpsDriveUrl } from "@/lib/api/gpsdrive-client";
import {
  fileToBase64,
  gpsDriveAiAssistantService,
  type AiMe,
  type AiMessage,
  type AiOutgoingAttachment,
} from "@/services/gpsdrive-ai-assistant.service";


interface Draft {
  id: string;
  name: string;
  mimeType: string;
  blob: Blob;
  preview?: string;
}

export const aiConversationKey = (id: string | null) => ["ai-assistant", "conversation", id] as const;

function pickRecorderMime(): string {
  const options = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
  for (const m of options) if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported?.(m)) return m;
  return "";
}

// **negrito**, listas e quebras de linha — o suficiente pro que o modelo devolve.
function RichText({ text }: { text: string }) {
  const lines = text.split("\n");
  return (
    <div className="space-y-1 break-words">
      {lines.map((line, i) => {
        const bullet = /^\s*[-*•]\s+/.test(line);
        const content = line.replace(/^\s*[-*•]\s+/, "").replace(/^#+\s*/, "");
        const parts = content.split(/(\*\*[^*]+\*\*)/g).map((p, j) =>
          p.startsWith("**") && p.endsWith("**") ? <strong key={j}>{p.slice(2, -2)}</strong> : <span key={j}>{p}</span>,
        );
        if (!content.trim()) return <div key={i} className="h-1" />;
        return bullet ? (
          <div key={i} className="flex gap-2">
            <span className="select-none">•</span>
            <span>{parts}</span>
          </div>
        ) : (
          <p key={i}>{parts}</p>
        );
      })}
    </div>
  );
}

export function AssistantAvatar({
  me,
  size = 32,
  glow = false,
  online = false,
}: {
  me: Pick<AiMe, "avatarUrl" | "name">;
  size?: number;
  /** anel colorido girando em volta (cabeçalho / boas-vindas) */
  glow?: boolean;
  online?: boolean;
}) {
  const inner = me.avatarUrl ? (
    <img
      src={resolveGpsDriveUrl(me.avatarUrl)}
      alt={me.name ?? "Assistente"}
      className="h-full w-full rounded-full object-cover"
    />
  ) : (
    <div
      className="grid h-full w-full place-items-center rounded-full ai-grad"
      style={{ fontSize: size * 0.5 }}
    >
      🤖
    </div>
  );
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      {glow && <span className="ai-halo absolute -inset-[3px] rounded-full" aria-hidden />}
      <div className={cn("relative h-full w-full rounded-full", glow ? "ring-2 ring-white dark:ring-slate-900" : "shadow-sm")}>{inner}</div>
      {online && (
        <span
          className="absolute bottom-0 right-0 rounded-full border-2 border-white bg-emerald-400 dark:border-slate-900"
          style={{ width: Math.max(9, size * 0.28), height: Math.max(9, size * 0.28) }}
        />
      )}
    </div>
  );
}

function MessageBubble({ m, me }: { m: AiMessage; me: AiMe }) {
  const mine = m.role === "user";
  if (m.status === "pending") {
    return (
      <div className="flex items-end gap-2">
        <AssistantAvatar me={me} size={28} />
        <div className="ai-in rounded-3xl rounded-bl-lg border border-white/60 bg-white/90 px-4 py-3.5 ai-text shadow-[0_6px_20px_-8px_rgba(15,23,42,0.35)] backdrop-blur dark:border-slate-700 dark:bg-slate-800/90">
          <span className="ai-typing flex gap-1">
            <i />
            <i />
            <i />
          </span>
        </div>
      </div>
    );
  }
  return (
    <div className={cn("ai-in flex items-end gap-2", mine && "justify-end")}>
      {!mine && <AssistantAvatar me={me} size={28} />}
      <div
        className={cn(
          "max-w-[85%] space-y-2 rounded-3xl px-4 py-2.5 text-[15px] leading-relaxed",
          mine
            ? "rounded-br-lg ai-grad shadow-[0_8px_22px_-10px_rgba(15,23,42,0.7)]"
            : m.status === "error"
              ? "rounded-bl-lg border border-red-200 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200"
              : "rounded-bl-lg border border-white/70 bg-white/95 text-slate-800 shadow-[0_6px_20px_-10px_rgba(15,23,42,0.25)] backdrop-blur dark:border-slate-700/70 dark:bg-slate-800/95 dark:text-slate-100",
        )}
      >
        {m.attachments.map((a, i) => {
          const url = resolveGpsDriveUrl(a.url);
          if (a.kind === "image") {
            return (
              <div key={i} className="space-y-1">
                <a href={url} target="_blank" rel="noreferrer">
                  <img src={url} alt={a.name} className="max-h-72 w-full rounded-2xl object-cover shadow-sm" loading="lazy" />
                </a>
                {a.generated && (
                  <a
                    href={url}
                    download={a.name}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-medium underline-offset-2 hover:underline"
                  >
                    <Download className="h-3.5 w-3.5" /> Baixar imagem
                  </a>
                )}
              </div>
            );
          }
          if (a.kind === "audio") {
            return (
              <div key={i} className="space-y-1">
                <audio controls src={url} className="h-9 w-56 max-w-full" preload="none" />
                {a.transcript && <p className="text-xs italic opacity-80">“{a.transcript}”</p>}
              </div>
            );
          }
          return (
            <a
              key={i}
              href={url}
              target="_blank"
              rel="noreferrer"
              className={cn(
                "flex items-center gap-2 rounded-xl px-2.5 py-2 text-sm",
                mine ? "bg-white/20" : "ai-soft ai-text dark:bg-slate-700",
              )}
            >
              <FileText className="h-4 w-4 shrink-0" />
              <span className="truncate">{a.name}</span>
            </a>
          );
        })}
        {m.content && (mine ? <p className="whitespace-pre-wrap break-words">{m.content}</p> : <RichText text={m.content} />)}
        <div className={cn("text-right text-[10px]", mine ? "opacity-70" : "text-slate-400")}>
          {new Date(m.createdAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
        </div>
      </div>
    </div>
  );
}

export function AssistantChat({
  me,
  conversationId,
  onConversationChange,
  className,
  header,
  bottomInset = 0,
  compactBottom = false,
  compact = false,
  suggestions,
  prefill,
  placeholder = "Escreva uma mensagem…",
}: {
  me: AiMe;
  conversationId: string | null;
  onConversationChange: (id: string) => void;
  className?: string;
  header?: ReactNode;
  /** px cobertos pelo teclado (ver use-keyboard-inset) */
  bottomInset?: number;
  /** barra de abas do app já cuida da safe-area de baixo */
  compactBottom?: boolean;
  /** Agenda inteligente (2026-10-21): só a última troca de mensagens + campo, sem a tela de boas-vindas */
  compact?: boolean;
  /** Ações rápidas acima do campo: send=true envia na hora, senão só preenche o texto */
  suggestions?: { label: string; message: string; send?: boolean }[];
  /** Texto colocado no campo por fora (ex.: "Editar" num compromisso); mude o nonce pra repetir */
  prefill?: { text: string; nonce: number } | null;
  placeholder?: string;
}) {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (!prefill) return;
    setText(prefill.text);
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(prefill.text.length, prefill.text.length);
    });
  }, [prefill]);
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recording, setRecording] = useState<{ startedAt: number } | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const cancelRecRef = useRef(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const conversation = useQuery({
    queryKey: aiConversationKey(conversationId),
    queryFn: () => gpsDriveAiAssistantService.conversation(conversationId!),
    enabled: Boolean(conversationId),
    refetchInterval: (q) => (q.state.data?.messages.some((m) => m.status === "pending") ? 1500 : false),
  });
  const messages = conversationId ? (conversation.data?.messages ?? []) : [];
  // Compacto (Agenda/Finanças, 2026-10-21): sem histórico — só a resposta
  // mais recente, flutuando por cima; some sozinha em 8 s (se não tiver
  // botões pra responder) ou no X.
  const [dismissedId, setDismissedId] = useState<string | null>(null);
  const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant");
  const compactShown = lastAssistant && lastAssistant.id !== dismissedId ? [lastAssistant] : [];
  const visibleMessages = compact ? compactShown : messages;
  useEffect(() => {
    if (!compact || !lastAssistant || lastAssistant.status === "pending" || lastAssistant.actions?.length) return;
    const t = setTimeout(() => setDismissedId(lastAssistant.id), 8000);
    return () => clearTimeout(t);
  }, [compact, lastAssistant?.id, lastAssistant?.status, lastAssistant?.actions?.length]);
  const busy = sending || messages.some((m) => m.status === "pending");

  // Resposta terminou: agenda/gastos podem ter mudado.
  const lastStatus = messages[messages.length - 1]?.status;
  useEffect(() => {
    if (lastStatus === "done" || lastStatus === "error") {
      qc.invalidateQueries({ queryKey: ["ai-assistant", "events"] });
      qc.invalidateQueries({ queryKey: ["ai-assistant", "expenses"] });
      qc.invalidateQueries({ queryKey: ["ai-assistant", "health"] });
      qc.invalidateQueries({ queryKey: ["ai-assistant", "conversations"] });
    }
  }, [lastStatus, qc]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages.length, lastStatus, sending, bottomInset]);

  useEffect(() => {
    if (!recording) return;
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - recording.startedAt) / 1000)), 250);
    return () => clearInterval(t);
  }, [recording]);

  useEffect(
    () => () => {
      cancelRecRef.current = true;
      recorderRef.current?.stop();
      stopVad();
    },
    [],
  );

  const send = async (extra: Draft[] = [], quick?: string) => {
    const body = (quick ?? text).trim();
    const all = [...drafts, ...extra];
    if ((!body && all.length === 0) || busy) return;
    setSending(true);
    setError(null);
    try {
      const attachments: AiOutgoingAttachment[] = await Promise.all(
        all.map(async (d) => ({ name: d.name, mimeType: d.mimeType, dataBase64: await fileToBase64(d.blob) })),
      );
      const res = await gpsDriveAiAssistantService.send({
        conversationId: conversationId ?? undefined,
        text: body,
        attachments,
      });
      if (quick === undefined) setText("");
      drafts.forEach((d) => d.preview && URL.revokeObjectURL(d.preview));
      setDrafts([]);
      qc.setQueryData(aiConversationKey(res.conversationId), (old: { messages: AiMessage[] } | undefined) => ({
        id: res.conversationId,
        title: "",
        ...old,
        messages: [...(old?.messages ?? []), res.userMessage, res.assistantMessage],
      }));
      if (res.conversationId !== conversationId) onConversationChange(res.conversationId);
      qc.invalidateQueries({ queryKey: aiConversationKey(res.conversationId) });
      qc.invalidateQueries({ queryKey: ["ai-assistant", "conversations"] });
    } catch (err) {
      setError(err instanceof GpsDriveApiError || err instanceof Error ? err.message : "Não foi possível enviar.");
    } finally {
      setSending(false);
    }
  };

  const startRecording = async () => {
    setError(null);
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setError("Este aparelho não permite gravar áudio aqui.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = pickRecorderMime();
      const rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      chunksRef.current = [];
      cancelRecRef.current = false;
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = () => {
        stopVad();
        stream.getTracks().forEach((t) => t.stop());
        setRecording(null);
        setElapsed(0);
        if (cancelRecRef.current) return;
        const type = (rec.mimeType || mimeType || "audio/webm").split(";")[0]!;
        const blob = new Blob(chunksRef.current, { type });
        if (blob.size < 1000) return;
        const ext = type.includes("mp4") ? "m4a" : type.includes("ogg") ? "ogg" : "webm";
        void send([{ id: crypto.randomUUID(), name: `audio.${ext}`, mimeType: type, blob }]);
      };
      recorderRef.current = rec;
      rec.start();
      setRecording({ startedAt: Date.now() });
      startVad(stream);
    } catch (err) {
      // APK até a 1.17 não tinha a permissão de microfone — o Android nem
      // chega a perguntar. Mostra a versão instalada pra saber qual é.
      const reason = err instanceof Error ? err.name : "";
      if (Capacitor.isNativePlatform()) {
        let build = "";
        try {
          const info = await App.getInfo();
          build = info.build;
          setError(
            Number(info.build) < 18
              ? `Seu aplicativo ainda é a versão ${info.version}, que não tem acesso ao microfone. Atualize o app para a versão 1.19 ou mais nova.`
              : `O microfone não foi liberado (${reason || "erro"}). Abra Configurações do celular → Apps → ${info.name} → Permissões → Microfone → Permitir. (versão ${info.version})`,
          );
        } catch {
          setError(`O microfone não foi liberado (${reason || "erro"}). Atualize o aplicativo para a versão mais nova.${build}`);
        }
      } else {
        setError("Permita o uso do microfone para gravar áudio (ícone de cadeado ao lado do endereço do site).");
      }
    }
  };

  // Envio automático (pedido 2026-10-21): depois que a pessoa começou a
  // falar, 2 s de silêncio encerram e enviam. Limite de 2 min por áudio.
  const vadRef = useRef<{ ctx: AudioContext; timer: ReturnType<typeof setInterval> } | null>(null);
  const [level, setLevel] = useState(0);
  const stopVad = () => {
    if (!vadRef.current) return;
    clearInterval(vadRef.current.timer);
    void vadRef.current.ctx.close().catch(() => undefined);
    vadRef.current = null;
    setLevel(0);
  };
  const startVad = (stream: MediaStream) => {
    try {
      const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      ctx.createMediaStreamSource(stream).connect(analyser);
      const buf = new Float32Array(analyser.fftSize);
      const started = Date.now();
      let noiseFloor = 0.01;
      let spoke = false;
      let lastVoice = Date.now();
      const timer = setInterval(() => {
        analyser.getFloatTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) sum += buf[i]! * buf[i]!;
        const rms = Math.sqrt(sum / buf.length);
        // Primeiro meio segundo calibra o ruído do ambiente.
        if (Date.now() - started < 500) noiseFloor = Math.max(noiseFloor, rms * 1.2);
        const voice = rms > Math.max(0.02, noiseFloor * 2.2);
        setLevel(Math.min(1, rms * 12));
        if (voice) {
          spoke = true;
          lastVoice = Date.now();
        } else if (spoke && Date.now() - lastVoice > 2000) {
          stopRecording(false);
        }
        if (Date.now() - started > 120_000) stopRecording(false);
      }, 100);
      vadRef.current = { ctx, timer };
    } catch {
      // sem Web Audio: segue só com o botão de enviar
    }
  };

  const stopRecording = (cancel: boolean) => {
    cancelRecRef.current = cancel;
    recorderRef.current?.stop();
    recorderRef.current = null;
  };

  const functions = me.functions ?? [];
  const { user } = useAuth();
  const firstName = useMemo(() => {
    const f = (user?.name ?? "").trim().split(/\s+/)[0] ?? "";
    return f ? f.charAt(0).toUpperCase() + f.slice(1).toLowerCase() : "";
  }, [user?.name]);

  return (
    <div className={cn("flex min-h-0 flex-col", compact ? "relative" : "flex-1", className)} style={aiPalette(me.themeColor)}>
      <style>{AI_STYLES}</style>
      {header}
      <div
        ref={scrollRef}
        className={cn(
          "min-h-0 space-y-3 overflow-y-auto px-3 text-slate-500",
          compact
            ? cn(
                "ai-in absolute inset-x-2 bottom-full mb-2 max-h-[40vh] rounded-3xl border ai-border bg-white/95 py-3 pr-9 shadow-[0_16px_40px_-14px_rgba(15,23,42,0.55)] backdrop-blur dark:border-slate-700 dark:bg-slate-900/95",
                visibleMessages.length === 0 && !sending && "hidden",
              )
            : "ai-chat-bg flex-1 py-4",
        )}
      >
        {compact && lastAssistant && (
          <button
            type="button"
            aria-label="Fechar resposta"
            onClick={() => setDismissedId(lastAssistant.id)}
            className="absolute right-2 top-2 grid h-7 w-7 place-items-center rounded-full bg-slate-100 text-slate-500 dark:bg-slate-800"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        {!conversationId && !compact && (
          <div className="ai-in space-y-5 pb-2 pt-2">
            <div className="flex flex-col items-center text-center">
              <div className="ai-float">
                <AssistantAvatar me={me} size={84} glow online />
              </div>
              <h3 className="mt-4 text-2xl font-extrabold tracking-tight ai-text">
                {firstName ? `Olá, ${firstName}!` : "Olá!"}
              </h3>
              <div className="mt-2 max-w-sm rounded-3xl border border-white/70 bg-white/80 px-4 py-3 text-[15px] leading-relaxed text-slate-700 shadow-[0_10px_30px_-15px_rgba(15,23,42,0.45)] backdrop-blur dark:border-slate-700/60 dark:bg-slate-800/80 dark:text-slate-200">
                <RichText text={me.welcomeMessage || `Eu sou ${me.name ?? "o assistente"}. Como posso ajudar?`} />
              </div>
            </div>
            {functions.length > 0 && (
              <div>
                <div className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wider ai-text">
                  ✨ Experimente
                </div>
                <div className="grid grid-cols-2 gap-2.5">
                  {functions.map((f, i) => (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => setText(SUGGESTION[f.tool] ?? `Quero ajuda com ${f.name.toLowerCase()}`)}
                      className="group relative overflow-hidden rounded-2xl border border-white/70 bg-white/85 p-3 text-left shadow-[0_8px_24px_-14px_rgba(15,23,42,0.35)] backdrop-blur transition active:scale-[0.98] dark:border-slate-700/60 dark:bg-slate-800/85"
                    >
                      <span
                        className={cn(
                          "mb-2 grid h-10 w-10 place-items-center rounded-xl text-xl shadow-sm",
                          CARD_GRADIENTS[i % CARD_GRADIENTS.length],
                        )}
                      >
                        {f.emoji}
                      </span>
                      {/* Só ícone + título (pedido 2026-10-21) — a descrição é pro agente, não pro cliente. */}
                      <div className="text-[13.5px] font-bold leading-tight text-slate-800 dark:text-slate-100">{f.name}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        {!compact && conversationId && conversation.isLoading && (
          <div className="flex justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        )}
        {visibleMessages.map((m, i) => (
          <div key={m.id}>
            <MessageBubble m={m} me={me} />
            {/* Botões só na última resposta — depois que o cliente responde, somem. */}
            {i === visibleMessages.length - 1 && m.role === "assistant" && m.status === "done" && !!m.actions?.length && (
              <div className="ai-in mt-2 flex flex-wrap gap-2 pl-9">
                {m.actions.map((a, k) => (
                  <button
                    key={k}
                    type="button"
                    disabled={busy}
                    onClick={() => void send([], a.message)}
                    className={cn(
                      "rounded-full border px-3.5 py-2 text-sm font-semibold shadow-sm transition active:scale-95 disabled:opacity-50",
                      k === 0 ? "ai-grad border-transparent" : "ai-border ai-text bg-white dark:bg-slate-800",
                    )}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
        {sending && !messages.some((m) => m.status === "pending") && (
          <MessageBubble
            m={{ id: "sending", role: "assistant", content: "", status: "pending", createdAt: "", attachments: [] }}
            me={me}
          />
        )}
      </div>

      <div
        className="bg-gradient-to-t from-white via-white/95 to-white/70 px-2 pt-2 backdrop-blur dark:from-slate-950 dark:via-slate-950/95 dark:to-slate-950/70"
        style={{
          paddingBottom: bottomInset
            ? `${bottomInset + 8}px`
            : compactBottom
              ? "0.5rem"
              : "calc(0.5rem + env(safe-area-inset-bottom))",
        }}
      >
        {error && <p className="px-2 pb-2 text-xs text-red-600 dark:text-red-400">{error}</p>}
        {me.configured === false && (
          <p className="px-2 pb-2 text-xs text-amber-700 dark:text-amber-300">
            Falta cadastrar a chave do ChatGPT em Administração → Assistente IA.
          </p>
        )}
        {!!suggestions?.length && !recording && (
          <div className="-mx-2 flex gap-2 overflow-x-auto px-3 pb-2 [scrollbar-width:none]">
            {suggestions.map((sg) => (
              <button
                key={sg.label}
                type="button"
                disabled={busy && sg.send}
                onClick={() => {
                  if (sg.send) void send([], sg.message);
                  else {
                    setText(sg.message);
                    requestAnimationFrame(() => textareaRef.current?.focus());
                  }
                }}
                className="shrink-0 rounded-full border ai-border bg-white px-3 py-1.5 text-[13px] font-semibold ai-text shadow-sm transition active:scale-95 disabled:opacity-50 dark:bg-slate-800"
              >
                {sg.label}
              </button>
            ))}
          </div>
        )}
        {drafts.length > 0 && (
          <div className="flex gap-2 overflow-x-auto px-1 pb-2">
            {drafts.map((d) => (
              <div key={d.id} className="relative shrink-0">
                {d.preview ? (
                  <img src={d.preview} alt="" className="h-16 w-16 rounded-lg object-cover" />
                ) : (
                  <div className="flex h-16 w-28 items-center gap-1.5 rounded-lg bg-slate-100 px-2 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                    <FileText className="h-4 w-4 shrink-0" />
                    <span className="line-clamp-2 break-all">{d.name}</span>
                  </div>
                )}
                <button
                  type="button"
                  aria-label="Remover anexo"
                  onClick={() => setDrafts((all) => all.filter((x) => x.id !== d.id))}
                  className="absolute -right-1.5 -top-1.5 grid h-5 w-5 place-items-center rounded-full bg-slate-900 text-white"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {recording ? (
          <div className="flex items-center gap-2 px-1 pb-1">
            <button
              type="button"
              aria-label="Cancelar gravação"
              onClick={() => stopRecording(true)}
              className="grid h-11 w-11 place-items-center rounded-full text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <Trash2 className="h-5 w-5" />
            </button>
            <div className="flex flex-1 items-center gap-2 rounded-full bg-red-50 px-4 py-2.5 text-sm font-medium text-red-700 dark:bg-red-950/40 dark:text-red-300">
              <span className="flex h-4 items-end gap-0.5" aria-hidden>
                {[0.6, 1, 0.75, 0.9, 0.5].map((k, i) => (
                  <i key={i} className="block w-1 rounded-full bg-red-500 transition-[height] duration-100" style={{ height: `${Math.max(3, level * k * 16)}px` }} />
                ))}
              </span>
              Ouvindo… {String(Math.floor(elapsed / 60)).padStart(1, "0")}:{String(elapsed % 60).padStart(2, "0")}
              <span className="ml-auto text-[11px] font-normal opacity-70">envia ao parar de falar</span>
            </div>
            <button
              type="button"
              aria-label="Enviar áudio"
              onClick={() => stopRecording(false)}
              className="grid h-11 w-11 place-items-center rounded-full ai-grad shadow-md"
            >
              <Square className="h-4 w-4 fill-current" />
            </button>
          </div>
        ) : (
          <div className="flex items-end gap-1 rounded-[28px] border ai-border bg-white p-1 shadow-[0_10px_30px_-12px_rgba(15,23,42,0.35)] dark:border-slate-700 dark:bg-slate-900">
            <textarea
              ref={textareaRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && window.matchMedia("(pointer: fine)").matches) {
                  e.preventDefault();
                  void send();
                }
              }}
              rows={1}
              placeholder={placeholder}
              className="max-h-32 min-h-11 flex-1 resize-none bg-transparent px-2 py-2.5 text-[15px] text-slate-900 outline-none placeholder:text-slate-400 dark:text-slate-100"
              style={{ fieldSizing: "content" } as React.CSSProperties}
            />
            {text.trim() || drafts.length ? (
              <button
                type="button"
                aria-label="Enviar"
                disabled={busy}
                onClick={() => void send()}
                className="grid h-11 w-11 shrink-0 place-items-center rounded-full ai-grad shadow-[0_6px_16px_-6px_rgba(15,23,42,0.8)] transition active:scale-95 disabled:opacity-50"
              >
                {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
              </button>
            ) : (
              <button
                type="button"
                aria-label="Gravar áudio"
                disabled={busy}
                onClick={() => void startRecording()}
                className="grid h-11 w-11 shrink-0 place-items-center rounded-full ai-grad shadow-[0_6px_16px_-6px_rgba(15,23,42,0.8)] transition active:scale-95 disabled:opacity-50"
              >
                <Mic className="h-5 w-5" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const CARD_GRADIENTS = ["ai-grad", "ai-grad-soft"];

// Estilos do Assistente — só variáveis prontas (ver ai-palette.ts), nada de
// color-mix (WebView antigo ignora e deixava tudo branco).
export const AI_STYLES = `
  .ai-typing i { width:7px; height:7px; border-radius:9999px; background:currentColor; opacity:.35; animation: ai-dot 1.2s infinite ease-in-out }
  .ai-typing i:nth-child(2){ animation-delay:.15s } .ai-typing i:nth-child(3){ animation-delay:.3s }
  @keyframes ai-dot { 0%,80%,100%{ transform:translateY(0); opacity:.35 } 40%{ transform:translateY(-4px); opacity:.9 } }
  .ai-grad { background-color: var(--ai); background-image: linear-gradient(135deg, var(--ai-dark), var(--ai) 55%, var(--ai-light)); color: var(--ai-on) }
  .ai-grad-soft { background-color: var(--ai); background-image: linear-gradient(135deg, var(--ai), var(--ai-light)); color: var(--ai-on) }
  .ai-halo { background: conic-gradient(from 0deg, var(--ai), var(--ai-light), #ffffff, var(--ai)); animation: ai-spin 4s linear infinite }
  @keyframes ai-spin { to { transform: rotate(360deg) } }
  .ai-in { animation: ai-in .32s cubic-bezier(.2,.8,.2,1) both }
  @keyframes ai-in { from { opacity:0; transform: translateY(8px) scale(.98) } to { opacity:1; transform:none } }
  .ai-float { animation: ai-float 3.4s ease-in-out infinite }
  @keyframes ai-float { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-6px) } }
  .ai-chat-bg {
    background-color: var(--ai-softer);
    background-image: radial-gradient(circle at 1px 1px, var(--ai-dot) 1px, transparent 0);
    background-size: 18px 18px;
  }
  .dark .ai-chat-bg { background-color: #0b1020; background-image: radial-gradient(circle at 1px 1px, rgba(148,163,184,.12) 1px, transparent 0) }
  .ai-text { color: var(--ai) }
  .ai-soft { background-color: var(--ai-soft) }
  .ai-border { border-color: var(--ai-border) }
  @media (prefers-reduced-motion: reduce) { .ai-typing i, .ai-halo, .ai-in, .ai-float { animation:none } }
`;

const SUGGESTION: Partial<Record<string, string>> = {
  agenda: "Anota pra mim: reunião amanhã às 15h, me lembra 30 minutos antes",
  gastos: "Gastei R$ 45,90 no almoço hoje",
};
